import imageCompression from "browser-image-compression";
import { Field, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { ImageCompressConfig } from "../../../constants/ImageCompressConfig";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { useAuth } from "../../auth/components/AuthInit";
import { TextArea } from "../../common/components/control/InputField";
import * as actions from "../redux/DashboardAction";

export const CommentField = ({ inboxId, setIsImageComment }) => {
  const { auth } = useAuth();
  const intl = useIntl();
  const dispatch = useDispatch();
  const formikRef = useRef();
  const fileInputRef = useRef();
  const [file, setFile] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
  const btnRef = useRef(null);
  const [imagePreview, setImagePreview] = useState([]);

  useEffect(() => {
    setIsImageComment(imagePreview.length > 0);
  }, [imagePreview]);

  useEffect(() => {
    if (inboxId) {
      setFile([]);
      setIsDisabled(true);
      setImagePreview([]);
      formikRef.current?.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [inboxId]);

  const handleFileChange = (event) => {
    const handleFile = (compressFile, fileName) => {
      const file = new File([compressFile], fileName, {
        type: compressFile.type,
      });
      setImagePreview((prev) =>
        prev.length < 3 ? [...prev, URL.createObjectURL(file)] : prev
      );
      setFile((prev) => (prev.length < 3 ? [...prev, file] : prev));
      setIsDisabled(false);
    };

    const fileName = event.currentTarget.files[0].name;

    imageCompression(event.currentTarget.files[0], ImageCompressConfig)
      .then((res) => handleFile(res, fileName))
      .catch(() => handleFile(event.currentTarget.files[0], fileName));
  };

  const createComment = (values) => {
    if (values.content.replace(/\s+/g, "").length > 0 || file?.length > 0) {
      const imagenameList = file
        ? file.map(
            (f, index) => `${Date.now() + index}.${f.name.split(".").at(-1)}`
          )
        : [];

      const newQueryParams = {
        action: "memberinboxcomment",
        memberid: auth.memberid,
        loginid: auth.loginid,
        projectid: auth.projectid,
        inboxid: inboxId,
        attchjson: file
          ? imagenameList.map((imagename) => ({ imagename }))
          : undefined,
        ...values,
      };

      const formData = new FormData();
      formData.append("imagenameList", JSON.stringify(imagenameList));
      file.forEach((f) => {
        formData.append("imageList[]", f);
      });
      formData.append("jsonData", JSON.stringify(newQueryParams));
      dispatch(actions.createComment(formData)).then((resp) => {
        resp = resp.data;

        if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
          setFile([]);
          setIsDisabled(true);
          setImagePreview([]);
          formikRef.current?.resetForm();
          if (fileInputRef.current) fileInputRef.current.value = "";
          dispatch(
            actions.getInboxDetail({
              action: "memberinboxdetail",
              inboxid: inboxId,
            })
          );
        }
      });
    }
  };

  return (
    <div
      className="px-2 pt-2 rounded position-absolute bottom-0 start-0 end-0 bg-dark"
      style={{ borderTop: "1px solid gray" }}
    >
      {imagePreview.length > 0 && (
        <div className="d-flex mb-2 gap-2">
          {imagePreview.map((image, index) => (
            <div
              key={index}
              style={{
                width: "35px",
                height: "35px",
              }}
              className="mb-2 position-relative d-flex align-items-center justify-content-center"
            >
              <img
                src={image}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
                onError={() => {
                  setImagePreview((prev) => prev.filter((_, i) => i !== index));
                  setFile((prev) => {
                    const newArr = prev.filter((_, i) => i !== index);
                    setIsDisabled(
                      newArr.length === 0 &&
                        formikRef.current?.values.content?.replace(/\s+/g, "")
                          ?.length === 0
                    );
                    return newArr;
                  });
                }}
              />

              <i
                className="position-absolute bi bi-x-circle-fill text-danger fs-5 cursor-pointer"
                onClick={() => {
                  setImagePreview((prev) => prev.filter((_, i) => i !== index));
                  setFile((prev) => {
                    const newArr = prev.filter((_, i) => i !== index);
                    setIsDisabled(
                      newArr.length === 0 &&
                        formikRef.current?.values.content?.replace(/\s+/g, "")
                          ?.length === 0
                    );
                    return newArr;
                  });
                }}
                style={{ top: "-5px", right: "-5px" }}
              />
            </div>
          ))}
        </div>
      )}
      <Formik
        initialValues={{
          content: "",
        }}
        innerRef={formikRef}
        enableReinitialize={true}
        onSubmit={createComment}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          touched,
          setTouched,
          errors,
          setFieldError,
        }) => (
          <Form>
            <Field
              name="content"
              component={TextArea}
              placeholder={intl.formatMessage({ id: "comment" })}
              autoComplete="off"
              inputClass="form-control"
              isAlphanumericOnly={false}
              rows={1}
              onChange={(value) => {
                setIsDisabled(
                  !(value.replace(/\s+/g, "").length > 0 || file?.length > 0)
                );
              }}
            />
          </Form>
        )}
      </Formik>
      <div className="d-flex justify-content-between align-items-center px-1">
        <input
          type="file"
          className="d-none"
          placeholder={intl.formatMessage({ id: "image" })}
          autoComplete="off"
          accept="image/*"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
        <i
          className="bi bi-card-image text-primary fs-4 mt-1"
          style={{ cursor: "pointer" }}
          onClick={() => fileInputRef.current?.click()}
        />
        <button
          type="button"
          className="btn btn-primary py-0"
          onClick={() => formikRef.current?.handleSubmit()}
          disabled={isDisabled}
        >
          {intl.formatMessage({ id: "create" })}
        </button>
      </div>
    </div>
  );
};
